"use client";

import {
  Children,
  cloneElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactElement,
  type ReactNode,
} from "react";
import { AuthUIContext } from "@daveyplate/better-auth-ui";

export const pgnPlayerNamesActionHref = "#user-pgn-player-names";

/** Handle this action through the installed menu item's selection event. */
export function PgnPlayerNamesAccountMenu({
  children,
  onOpen,
}: {
  children: ReactNode;
  onOpen: () => void;
}) {
  const inherited = useContext(AuthUIContext);
  const pendingOpen = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(pendingOpen.current), []);
  const value = useMemo(() => {
    const InheritedLink = inherited.Link;
    const Link: typeof inherited.Link = (props) => {
      if (props.href !== pgnPlayerNamesActionHref)
        return <InheritedLink {...props} />;
      const item = Children.only(props.children) as ReactElement<{
        onSelect?: (event: Event) => void;
      }>;
      return cloneElement(item, {
        onSelect: (event) => {
          item.props.onSelect?.(event);
          // Let the installed menu release its modal focus scope before opening
          // the editor (the two primitives have separate Radix dependency copies).
          if (!event.defaultPrevented) {
            clearTimeout(pendingOpen.current);
            pendingOpen.current = setTimeout(() => {
              pendingOpen.current = undefined;
              onOpen();
            }, 0);
          }
        },
      });
    };
    return { ...inherited, Link };
  }, [inherited, onOpen]);
  return (
    <AuthUIContext.Provider value={value}>{children}</AuthUIContext.Provider>
  );
}
